/**
 * 주간 보고 문서에 올리는 보고.
 * 문장·이름·일정·공정은 주간 보고 아카이브(한빛제조, 기준일 2026-10-23)를 따른다.
 */
import { addDays } from "@/engine/date";
import type {
  ISODate,
  WeeklyAgendaLine,
  WeeklyIssueLine,
  WeeklyLineOrigin,
  WeeklyRequestLine,
  WeeklySectionVisibility,
  WeeklyTaskLine,
  WeeklyWbsLine,
} from "@/engine/types";
import type { CostEvm } from "@/features/pm/cost-evm";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { WEEKLY_REPORT } from "./weekly-report-mock";
import {
  SHIFT_LABEL,
  decisionsForWeek,
  issuesForWeek,
  issueTrend,
  personById,
  weeksThrough,
  type WeeklySeverity,
  type WeeklySnapshot,
  type WeeklyWorkstream,
} from "./weekly-report-model";

export interface WeeklyV2DisciplineInfo {
  deliverable?: string;
  task?: string;
}

export interface WeeklyV2WbsRow {
  id: string;
  isPhase: boolean;
  code: string;
  name: string;
  planStart: string;
  planEnd: string;
  actualStart: string;
  actualEnd: string;
  progress: number;
  pm: WeeklyV2DisciplineInfo;
  design: WeeklyV2DisciplineInfo;
  dev: WeeklyV2DisciplineInfo;
  /** RACI의 A. 없으면 담당. */
  accountable?: string;
}

export interface WeeklyV2AgendaItem {
  id: string;
  sourceId?: string;
  date: string;
  rawDate?: ISODate;
  title: string;
  owner: string;
  type: string;
  status: "done" | "upcoming" | "shifted";
  origin?: WeeklyLineOrigin;
  carryOver?: boolean;
  missingSource?: boolean;
}

export interface WeeklyV2IssueItem {
  id: string;
  sourceId?: string;
  code: string;
  date: string;
  openedDate?: ISODate;
  title: string;
  severity: "High" | "Medium" | "Low" | null;
  owner: string;
  action: string;
  status: "open" | "inProgress" | "resolved" | "delayed";
  origin?: WeeklyLineOrigin;
  carryOver?: boolean;
  missingSource?: boolean;
}

export interface WeeklyV2RequestItem {
  id: string;
  sourceId?: string;
  code: string;
  date: string;
  title: string;
  neededBy: string;
  target: string;
  status: "pending" | "approved" | "reviewing" | "overdue";
  impact: string;
  origin?: WeeklyLineOrigin;
  carryOver?: boolean;
  missingSource?: boolean;
}

export interface WeeklyV2WeekSnapshot {
  weekId: string;
  weekNumber: number;
  weekLabel: string;
  /** 문서 제목. 예: "10월 3주차 주간 보고" */
  title?: string;
  statusDate?: ISODate;
  periodLabel: string;
  asOfDate: string;
  reportNo: string;
  version: string;
  /** 숫자 버전 (1, 2, ...). */
  versionNumber?: number;
  closedAt?: string;
  projectName: string;
  projectPeriod: string;
  clientPm: string;
  vendorPm: string;
  author: string;
  approvers: string[];
  /** 구역 표시 여부. 마감본에 함께 저장한다. */
  sections?: WeeklySectionVisibility;
  planProgress: number;
  actualProgress: number;
  /** 없으면 계획 대비를 숫자로 보여 준다. false면 기준선이 없어 계획·차이는 「—」다. */
  hasBaseline?: boolean;
  computedVerdict?: "normal" | "watch" | "critical" | "unknown";
  statusVerdict: "normal" | "watch" | "critical" | "unknown";
  /** 작성자가 평가를 덮어썼으면 true. */
  verdictEdited?: boolean;
  statusSummary: string;
  /** 그 주를 한 단락으로 적은 평가. */
  weekMemo?: string;
  /** 미결 이슈·리스크의 연령 구간. */
  agingNote?: string;
  prevTasksRange?: string;
  prevTasks?: string[];
  prevTaskLines?: WeeklyTaskLine[];
  currentTasksRange: string;
  currentTasks: string[];
  currentTaskLines?: WeeklyTaskLine[];
  nextTasksRange: string;
  nextTasks: string[];
  nextTaskLines?: WeeklyTaskLine[];
  agendas: WeeklyV2AgendaItem[];
  agendaLines?: WeeklyAgendaLine[];
  issues: WeeklyV2IssueItem[];
  issueLines?: WeeklyIssueLine[];
  requests: WeeklyV2RequestItem[];
  requestLines?: WeeklyRequestLine[];
  wbs: WeeklyV2WbsRow[];
  wbsLines?: WeeklyWbsLine[];
  /** 비용 EVM. 없으면 화면은 판정 불가. 표시가 꺼져 있어도 마감본에 포함된다. */
  costEvm?: CostEvm;
  costNote?: string;
  /** 마감 시점의 주차별 이력 복사본. */
  history?: WeeklyV2HistoryPoint[];
}

export interface WeeklyV2HistoryPoint {
  weekId: string;
  statusDate?: ISODate;
  weekLabel: string;
  period: string;
  planProgress: number;
  actualProgress: number;
  gap: number;
  /** false면 계획·차이는 숫자가 아니다. */
  hasBaseline?: boolean;
  openIssues: number;
  resolvedIssues: number;
  pendingRequests: number;
  status: "normal" | "watch" | "critical" | "unknown";
  statusLabel: string;
  /** 현재 초안 줄이면 true. */
  isDraft?: boolean;
}

const dash: WeeklyV2DisciplineInfo = { deliverable: "—", task: "—" };

function job(deliverable: string, task: string): WeeklyV2DisciplineInfo {
  return { deliverable, task };
}

function wbsRow(
  id: string,
  isPhase: boolean,
  code: string,
  name: string,
  planStart: string,
  planEnd: string,
  pm: WeeklyV2DisciplineInfo = dash,
  design: WeeklyV2DisciplineInfo = dash,
  dev: WeeklyV2DisciplineInfo = dash,
): WeeklyV2WbsRow {
  const done = id.startsWith("3") || id === "p1" || id === "p2" || id === "p3";
  return {
    id,
    isPhase,
    code,
    name,
    planStart,
    planEnd,
    actualStart: done ? planStart : "—",
    actualEnd: done ? planEnd : "—",
    progress: done ? 100 : 0,
    pm,
    design,
    dev,
  };
}

/** 단계 줄의 자리. 공정 숫자는 그 주 스냅샷으로 덮는다. */
const WBS_TEMPLATE: WeeklyV2WbsRow[] = [
  wbsRow("p1", true, "1.", "착수", "2026-07-06", "2026-07-06", job("착수보고", "범위와 일정을 잠급니다")),
  wbsRow("p2", true, "2.", "분석", "2026-07-07", "2026-08-14", job("요구사항", "업무 인터뷰"), job("업무 흐름", "화면 후보")),
  wbsRow("p3", true, "3.", "설계", "2026-08-17", "2026-09-18", job("설계서", "화면과 기능을 잠급니다"), job("화면 시안", "주요 화면"), job("설계 기준", "테이블과 연계")),
  wbsRow("3.1", false, "3.1", "기준정보 체계", "2026-08-17", "2026-08-28", job("기준정보 정의서", "코드 체계를 잠급니다"), job("정보 구조", "코드 자리")),
  wbsRow("3.2", false, "3.2", "기능정의", "2026-08-17", "2026-09-04", job("기능정의서", "발주·입고·실적 흐름"), dash, job("연계 요건", "생산 실적 항목")),
  wbsRow("3.3", false, "3.3", "화면 시안", "2026-09-01", "2026-09-11", job("시연 기록", "화면 초안을 검토합니다"), job("화면 시안", "발주·출하 화면")),
  wbsRow("3.4", false, "3.4", "화면설계", "2026-09-04", "2026-09-18", job("스토리보드", "구매·영업·결산 화면"), dash, job("화면 명세", "입력 항목")),
  wbsRow("3.5", false, "3.5", "개발 표준", "2026-08-24", "2026-09-11", dash, dash, job("개발 표준", "이름 규칙과 오류 처리")),
  wbsRow("3.6", false, "3.6", "전환 설계", "2026-09-08", "2026-09-18", dash, dash, job("매핑 정의", "원천과 목표 열")),
  wbsRow("3.7", false, "3.7", "설계 종료 점검", "2026-09-18", "2026-09-18", job("설계 종료 점검", "산출물을 잠급니다"), job("설계 편철", "화면 시안을 묶습니다")),
  wbsRow("p4", true, "4.", "구축", "2026-09-21", "2026-11-20", job("진척", "모듈별 공정"), job("화면", "구축 중 화면"), job("연계", "모듈 개발")),
  wbsRow("4.1", false, "4.1", "기준정보", "2026-09-21", "2026-10-09", job("확정 회의", "기준정보 일정"), job("코드 대조", "구 코드와 신 코드"), job("이관", "코드 값")),
  wbsRow("4.2", false, "4.2", "구매·자재", "2026-09-21", "2026-11-13", job("공정 점검", "발주 코드"), job("발주 화면", "입고 예외"), job("연동", "발주·입고")),
  wbsRow("4.3", false, "4.3", "생산", "2026-09-28", "2026-11-13", dash, dash, job("실적 인터페이스", "생산 값")),
  wbsRow("4.4", false, "4.4", "영업·출하", "2026-10-05", "2026-11-20", dash, job("출하 화면", "출하 흐름"), job("화면 착수", "출하")),
  wbsRow("4.5", false, "4.5", "결산 연계", "2026-10-05", "2026-11-13", job("범위 합의", "결산 연계"), dash, job("연계 개발", "범위가 정해진 뒤")),
  wbsRow("4.6", false, "4.6", "데이터 전환", "2026-09-21", "2026-11-06", dash, dash, job("전환 매핑", "빈 열")),
  wbsRow("p5", true, "5.", "테스트", "2026-10-12", "2026-11-27", job("테스트 계획", "통합 테스트"), dash, job("테스트 환경", "시나리오")),
  wbsRow("5.1", false, "5.1", "통합 테스트", "2026-10-12", "2026-11-20", job("착수 보고", "통합 시나리오"), dash, job("테스트 환경", "시나리오")),
  wbsRow("5.2", false, "5.2", "이행", "2026-11-02", "2026-11-27", job("이행 계획", "오픈 전 점검"), dash, job("이관", "전환 순서")),
  wbsRow("5.3", false, "5.3", "사용자 교육", "2026-11-06", "2026-11-06", job("교육 일정", "사용자 교육"), job("교육 교재", "교육 대상")),
  wbsRow("p6", true, "6.", "오픈", "2026-11-27", "2026-12-11", job("완료보고", "오픈과 안정화")),
  wbsRow("6.1", false, "6.1", "산출물 통합", "2026-11-16", "2026-12-04", job("산출물 목록", "단계별 산출물")),
  wbsRow("6.2", false, "6.2", "고객 검수", "2026-11-27", "2026-12-08", job("검수 확인", "오픈 기준")),
  wbsRow("6.3", false, "6.3", "완료보고", "2026-12-11", "2026-12-11", job("완료보고", "안정화 종료")),
];

const STREAM_ROW: Record<string, string> = {
  "4.1": "master",
  "4.2": "procure",
  "4.3": "prod",
  "4.4": "sales",
  "4.5": "close",
  "4.6": "data",
  "5.1": "test",
};

function phaseProgress(week: WeeklySnapshot, id: string): number {
  return week.phases.find((phase) => phase.id === id)?.progress ?? 0;
}

function streamOf(week: WeeklySnapshot, id: string): WeeklyWorkstream | undefined {
  return week.workstreams.find((item) => item.id === id);
}

function startedOn(weeks: readonly WeeklySnapshot[], test: (week: WeeklySnapshot) => boolean): string {
  return weeks.find(test)?.period.start ?? "—";
}

function withProgress(row: WeeklyV2WbsRow, progress: number, actualStart: string, doneEnd?: string): WeeklyV2WbsRow {
  if (progress <= 0) return { ...row, progress: 0, actualStart: "—", actualEnd: "—" };
  if (progress >= 100) {
    return { ...row, progress: 100, actualStart, actualEnd: doneEnd && doneEnd !== "—" ? doneEnd : row.planEnd };
  }
  return { ...row, progress, actualStart, actualEnd: "진행중" };
}

function wbsFor(week: WeeklySnapshot): WeeklyV2WbsRow[] {
  const through = weeksThrough(WEEKLY_REPORT, week.id);
  const buildStart = startedOn(through, (item) => phaseProgress(item, "build") > 0);
  const testStart = startedOn(through, (item) => phaseProgress(item, "test") > 0);
  const cutoverStart = startedOn(through, (item) => phaseProgress(item, "cutover") > 0);
  const masterDone = week.milestones.find((item) => item.id === "master")?.actual;

  return WBS_TEMPLATE.map((row) => {
    if (row.id === "p1") return { ...row, progress: phaseProgress(week, "kickoff") };
    if (row.id === "p2") return { ...row, progress: phaseProgress(week, "analysis") };
    if (row.id === "p3") return { ...row, progress: phaseProgress(week, "design") };
    if (row.id === "p4") return withProgress(row, phaseProgress(week, "build"), buildStart);
    if (row.id === "p5") return withProgress(row, phaseProgress(week, "test"), testStart);
    if (row.id === "5.2") return withProgress(row, phaseProgress(week, "cutover"), cutoverStart);
    const streamId = STREAM_ROW[row.id];
    if (!streamId) return row;
    const actual = streamOf(week, streamId)?.actual ?? 0;
    const start = startedOn(through, (item) => (streamOf(item, streamId)?.actual ?? 0) > 0);
    return withProgress(row, actual, start, streamId === "master" ? masterDone : undefined);
  });
}

function personLabel(id: string): string {
  const person = personById(WEEKLY_REPORT, id);
  return person.role ? `${person.name} (${person.role})` : person.name;
}

const SEVERITY: Record<WeeklySeverity, WeeklyV2IssueItem["severity"]> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

const ISSUE_CODE = new Map(WEEKLY_REPORT.issues.map((issue, index) => [issue.id, `ISS-${String(index + 1).padStart(2, "0")}`]));
const REQUEST_CODE = new Map(WEEKLY_REPORT.decisions.map((decision, index) => [decision.id, `REQ-${String(index + 1).padStart(2, "0")}`]));

function issueAction(row: ReturnType<typeof issuesForWeek>[number]): string {
  if (row.state === "done") {
    const when = row.closed ? `${formatDate(row.closed, "full")}에 닫았습니다.` : "닫았습니다.";
    return row.note ? `${when} ${row.note}` : when;
  }
  const parts: string[] = [];
  if (row.note) parts.push(row.note);
  if (row.due) {
    const due = formatDate(row.due, "full");
    parts.push(row.state === "late" ? `기한인 ${due}이 지났습니다.` : `기한은 ${due}입니다.`);
  }
  if (row.escalated) parts.push("상신이 필요합니다.");
  return parts.join(" ");
}

function requestImpact(row: ReturnType<typeof decisionsForWeek>[number]): string {
  const state = row.state === "overdue" ? "필요일이 지났습니다." : "결정을 기다리는 중입니다.";
  const file = row.attachments?.join(", ");
  return file ? `${state} ${file}` : state;
}

function verdictOf(rag: WeeklySnapshot["rag"]): WeeklyV2WeekSnapshot["statusVerdict"] {
  if (rag === "offTrack") return "critical";
  if (rag === "watch") return "watch";
  return "normal";
}

function buildSnapshot(week: WeeklySnapshot): WeeklyV2WeekSnapshot {
  const issues = issuesForWeek(WEEKLY_REPORT, week).map((row) => ({
    id: row.id,
    code: ISSUE_CODE.get(row.id) ?? row.id,
    date: formatDate(row.opened, "numeric"),
    title: row.title,
    severity: SEVERITY[row.severity],
    owner: personLabel(row.ownerId),
    action: issueAction(row),
    status: row.state === "done" ? "resolved" as const : row.state === "late" ? "delayed" as const : "open" as const,
  }));
  const requests = decisionsForWeek(WEEKLY_REPORT, week)
    .filter((row) => row.state !== "done")
    .map((row) => ({
      id: row.id,
      code: REQUEST_CODE.get(row.id) ?? row.id,
      date: formatDate(row.asked, "numeric"),
      title: row.title,
      neededBy: formatDate(row.neededBy, "numeric"),
      target: personLabel(row.ownerId),
      status: "pending" as const,
      impact: requestImpact(row),
    }));

  return {
    weekId: week.id,
    weekNumber: week.index,
    weekLabel: week.label,
    periodLabel: formatDateRange(week.period.start, week.period.end, "numeric"),
    asOfDate: formatDate(week.asOf, "fullWeekday"),
    reportNo: `HB-ERP-W${String(week.index).padStart(2, "0")}`,
    version: formatDate(week.asOf, "numeric"),
    projectName: `[${WEEKLY_REPORT.clientName}] ${WEEKLY_REPORT.projectName}`,
    projectPeriod: `${formatDateRange(WEEKLY_REPORT.period.start, WEEKLY_REPORT.period.end, "full")} (${WEEKLY_REPORT.period.span})`,
    clientPm: personLabel("client"),
    vendorPm: personLabel("pm"),
    author: personLabel(week.authorId),
    approvers: week.approverIds.map(personLabel),
    planProgress: week.measures.planProgress,
    actualProgress: week.measures.actualProgress,
    statusVerdict: verdictOf(week.rag),
    statusSummary: week.ragReason,
    currentTasksRange: formatDateRange(week.period.start, week.period.end, "short"),
    currentTasks: week.workstreams.filter((item) => item.thisWeek).map((item) => `${item.name}: ${item.thisWeek}`),
    nextTasksRange: formatDateRange(addDays(week.period.end, 1), addDays(week.period.end, 7), "short"),
    nextTasks: week.workstreams.filter((item) => item.nextWeek).map((item) => `${item.name}: ${item.nextWeek}`),
    agendas: week.agenda.map((item) => ({
      id: item.id,
      date: formatDate(item.date, "numeric"),
      title: item.title,
      owner: personLabel(item.ownerId),
      type: SHIFT_LABEL[item.shift],
      status: item.shift === "done" ? "done" : item.shift === "held" ? "upcoming" : "shifted",
    })),
    issues,
    requests,
    wbs: wbsFor(week),
  };
}

const SNAPSHOTS = WEEKLY_REPORT.weeks.map(buildSnapshot);

export const WEEKLY_V2_SNAPSHOTS: Record<string, WeeklyV2WeekSnapshot> = Object.fromEntries(
  SNAPSHOTS.map((snapshot) => [snapshot.weekId, snapshot]),
);

export const WEEKLY_V2_LATEST_ID = SNAPSHOTS[SNAPSHOTS.length - 1]?.weekId ?? "w6";

/** 디자인 시스템 견본. 마지막 주(10월 4주차) WBS다. */
export const WBS_SNAPSHOT_WEEK_10: WeeklyV2WbsRow[] = WEEKLY_V2_SNAPSHOTS[WEEKLY_V2_LATEST_ID]?.wbs ?? [];

const trend = issueTrend(WEEKLY_REPORT);

export const WEEKLY_V2_HISTORY: WeeklyV2HistoryPoint[] = WEEKLY_REPORT.weeks.map((week, index) => {
  const point = trend[index];
  const pending = decisionsForWeek(WEEKLY_REPORT, week).filter((row) => row.state !== "done").length;
  return {
    weekId: week.id,
    weekLabel: week.label,
    period: formatDateRange(week.period.start, week.period.end, "numeric"),
    planProgress: week.measures.planProgress,
    actualProgress: week.measures.actualProgress,
    gap: week.measures.actualProgress - week.measures.planProgress,
    openIssues: point?.open ?? 0,
    resolvedIssues: point?.closed ?? 0,
    pendingRequests: pending,
    status: verdictOf(week.rag),
    statusLabel: week.ragReason,
  };
});
